/*
 * Cadrul aplicatiei: navigatia laterala, bara de sus pe ecran mic si zona de
 * continut.
 *
 * Navigatie laterala si nu meniu orizontal in antet: sectiunile poarta contoare
 * ("Cart 3"), iar acelea trebuie sa fie vizibile tot timpul, nu doar cat esti pe
 * ecranul lor. Pe latime, contoarele au unde sa stea fara sa imbulzeasca
 * etichetele; intr-un antet ar fi trebuit micsorate pana la ilizibil.
 *
 * Sub 900px navigatia devine un panou care se deschide din bara de sus. Nu se
 * ascunde in "hamburger" si pe desktop: cand exista loc, un meniu mereu vizibil
 * scuteste un clic la fiecare navigare.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import cecLogo from '../assets/cec-logo.svg';
import odLogo from '../assets/optimumdesk-logo.png';
import { useAuth } from '../auth/AuthContext';
import { t } from '../lib/i18n';
import { useCounts } from './CountsContext';
import { LangSwitch } from './LangSwitch';
import { Icon, type IconName } from './Icon';
import { ExternalLink } from './ExternalLink';
import { privacyUrl } from '../lib/privacy';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  count: number | null;
  /** Se deseneaza cu rosu. O singura intrare are voie - vezi lista de mai jos. */
  danger?: boolean;
  /**
   * Cate raspunsuri noi, pe randul asta.
   *
   * Cand e peste zero, insigna randului devine un CLOPOTEL cu numarul in el, in
   * locul numaratorii obisnuite. Nu se arata amandoua: doua insigne pe acelasi
   * rand - cate ai si la cate ti s-a raspuns - se citesc ca un singur lucru
   * scris de doua ori, iar cea care cheama omul acum e a doua.
   *
   * Numarul de cereri nu se pierde: sta in chiar titlul paginii „Cererile mele".
   */
  replies?: number | null;
}

export function AppShell() {
  const { user, signOut } = useAuth();
  const counts = useCounts();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  /* Schimbarea limbii nu trece prin nicio stare React - `t` citeste o variabila
     de modul. Numarul asta e tot ce leaga cele doua lumi: se schimba, cadrul se
     redeseneaza, si `key` de mai jos remonteaza ecranul din interior cu textele
     noi. Vezi lib/i18n.ts. */
  const [nonce, setNonce] = useState(0);

  /* Panoul de pe mobil se inchide la navigare. Fara asta, omul apasa un link,
     ecranul din spate se schimba si el ramane cu meniul peste el. */
  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  /* Trei sectiuni, aceleasi pentru toata lumea. Nu mai exista intrare de
     aprobari si nici ramura pe `can.approve`: aprobarea e o mutare de stare pe
     tichet, in platforma, deci nu se mai decide nimic in magazin.

     A patra intrare, „Cerere", e altfel si se vede altfel: nu e o sectiune de
     rasfoit, e usa pentru ce NU e in catalog - un tichet liber, cu subiect si
     descriere. Rosul e cerut anume si ramane singurul din meniu: doua intrari
     rosii n-ar mai evidentia niciuna. */
  const items: NavItem[] = [
    /*
     * PRIMA, si nu din intamplare.
     *
     * Cine deschide catalogul vine cu o nevoie, nu cu pofta de a rasfoi un
     * raft. Ecranul asta o intreaba direct - "cum te putem ajuta astazi?" - si
     * cauta singur in articole si in catalog. Pus ultimul, il gaseau numai cei
     * care esuasera deja in celelalte.
     */
    { to: '/tickets/new', label: t('Your request'), icon: 'ticket', count: null },
    { to: '/catalog', label: t('Catalog'), icon: 'catalog', count: null },
    { to: '/help', label: t('Help articles'), icon: 'help', count: null },
    { to: '/cart', label: t('Cart'), icon: 'cart', count: counts.cart },
    /*
     * Clopotelul sta PE „Cererile mele", nu pe un rand al lui.
     *
     * A fost o intrare separata, si arata a a doua sectiune - dar nu era una:
     * ducea in acelasi loc. Doua randuri catre aceeasi pagina il pun pe om sa
     * aleaga intre ele, iar dupa ce citeste raspunsurile unul din doua dispare
     * si meniul se misca sub mana lui.
     *
     * Acum e o stare a randului: cat timp cineva ti-a scris, insigna e un
     * clopotel cu numarul in el; altfel, numaratoarea obisnuita.
     */
    {
      to: '/requests',
      label: t('My requests'),
      icon: 'requests',
      count: counts.requests,
      replies: counts.newReplies,
    },
  ];

  return (
    <div className={`shell${menuOpen ? ' shell--menu-open' : ''}`}>
      <a className="skip-link" href="#main">
        {t('Skip to content')}
      </a>

      <header className="topbar">
        <button
          type="button"
          className="topbar__toggle"
          aria-expanded={menuOpen}
          aria-controls="primary-nav"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <Icon name={menuOpen ? 'close' : 'menu'} />
          <span className="sr-only">{menuOpen ? t('Close menu') : t('Open menu')}</span>
        </button>
        <span className="topbar__title">{t('IT Service Catalog')}</span>
      </header>

      <nav className="sidebar" id="primary-nav" aria-label="Primary">
        <div className="sidebar__brand">
          {/* Marca bancii in locul celor doua bare desenate de noi. Mica
              dinadins: aici spune al cui e catalogul, nu se prezinta - cine e
              pe ecran stie deja unde lucreaza. */}
          <img className="sidebar__client" src={cecLogo} alt="CEC Bank" />
          <span className="brand__text">
            {/* Marca, pe doua randuri, dintr-o singura traducere taiata la `|`.
                Doua chei separate nu merg: a doua ar fi fost chiar "Catalog",
                si aia e deja eticheta din meniu - traducand-o pentru marca, se
                schimba si acolo. Cu bara, taietura sta in traducere, unde si
                trebuie: in engleza cade dupa "IT Service", in romana dupa
                "Catalog de". */}
            {t('IT Service|Catalog')
              .split('|')
              .map((line, i) => (
                <span className={`brand__line${i + 1}`} key={line}>
                  {line}
                </span>
              ))}
          </span>
        </div>

        <ul className="nav">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={item.danger ? 'nav__link nav__link--danger' : 'nav__link'}
              >
                <Icon name={item.icon} />
                <span className="nav__label">{item.label}</span>
                {(item.replies ?? 0) > 0 ? (
                  /*
                   * Clopotel cu numarul in el, si un nume citit cu voce tare -
                   * altfel un cititor de ecran ar fi anuntat doar o cifra, si
                   * omul n-ar fi stiut a ce.
                   */
                  <span className="nav__count nav__count--bell">
                    <Icon name="bell" />
                    {item.replies}
                    <span className="sr-only"> {t('new replies')}</span>
                  </span>
                ) : item.count !== null && item.count > 0 ? (
                  <span className="nav__count">{item.count}</span>
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="sidebar__account">
          <span className="micro-label">{t('Signed in as')}</span>
          <span className="sidebar__name">{user?.name ?? user?.email ?? t('Unknown user')}</span>
          {user?.email ? <span className="sidebar__email">{user.email}</span> : null}

          <LangSwitch onChange={() => setNonce((n) => n + 1)} />

          <button
            type="button"
            className="btn btn--sm btn--block"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              void signOut().finally(() => setSigningOut(false));
            }}
          >
            {signingOut ? t('Signing out…') : t('Sign out')}
          </button>
        </div>

        {/* Semnatura furnizorului, la baza barei.
            Jos de tot si stinsa: e o mentiune, nu o informatie de care are
            nevoie cineva ca sa-si duca treaba la capat. Sub contul omului,
            fiindca dupa butonul de deconectare nu mai urmeaza nimic de facut. */}
        {/* Politica de confidentialitate.
            Ceruta de Google Play si App Store ca legatura accesibila DIN
            aplicatie, nu doar din fisa magazinului. Sta langa semnatura
            furnizorului, unde se cauta in mod firesc mentiunile legale, si e
            in limba interfetei - vezi lib/privacy.ts. */}
        {privacyUrl() ? (
          <ExternalLink className="sidebar__legal" href={privacyUrl()}>
            {t('Privacy policy')}
          </ExternalLink>
        ) : null}

        <div className="sidebar__vendor">
          <img src={odLogo} alt="OptimumDesk" />
        </div>
      </nav>

      {/* Fundalul care inchide panoul pe mobil. Nu e focusabil: Escape si
          butonul din bara raman caile de inchis de la tastatura. */}
      <div className="shell__scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />

      <main className="content" id="main" tabIndex={-1}>
        {/* `key` pe nonce: la schimbarea limbii, ecranul dinauntru se remonteaza
            si isi reciteste textele. Ecranele isi refac si cererile catre api -
            asumat, se intampla la un clic pe care nimeni nu-l apasa des. */}
        <div key={nonce} className="content__inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
